import Card from '@mui/material/Card'
import TextField from '@mui/material/TextField'
import React, {
  FormEvent,
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactNode,
  ReactElement,
} from 'react'
import { useTranslation } from 'react-i18next'
import { TextButtonVariant } from '../Buttons/TextButton'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import Header, { HeaderVariant } from '../Elements/Header'
import CardFormHeader from './CardFormHeader'
import Logo from '../../Images/Branding/cc-logo-white.svg'
import { todaysDateFormattedLocal } from '../../utils/todaysDateFormatted'
import {
  timeStringToNumber,
  timezoneTitleCase,
} from '../../utils/timeFormatUtilities'
import { useNavigate, useLocation } from 'react-router'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import { Address, EventEventTypeEnum } from '../../swagger'
import { events, extractedErrorObject, timezones } from '../../api/swagger'
import useValidationMessages from '../../hooks/useValidationMessages'
import RegistrantsTable from '../Events/RegistrantsTable'
import { Event } from '../../swagger'
import { Timezones } from '../../swagger/models/Timezones'
import spacetime from 'spacetime'
import { Registrant } from '../../swagger/models/Registrant'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import { validateEmailText } from '../../helpers/validateEmail'
import AddressModal from '../Address/AddressModal'
import ErrorAlert from '../Alerts/ErrorAlert'
import { useAuth } from '../Routes/AuthProvider'
import ConfirmationModal from '../Modals/ConfirmationModal'
import ActionButtons from '../Buttons/ActionButtons'
import { FieldValidity } from '../Interfaces/FieldValidity'
import Box from '@mui/material/Box'
import useEmailMessage from '../../hooks/useEmailMessage'
import { useSnackbarContext } from '../Context/SnackbarContext'
import DateField, { DateFieldVariant } from '../Elements/DateField'
import {
  FormControl,
  FormGroup,
  FormLabel,
  SelectChangeEvent,
} from '@mui/material'
import { useNavigationAwayContext } from '../Context/NavigationAwayContext'
import { useNavigationListener } from '../../hooks/useNavigationListener'
import { useMountEffect } from '../../hooks/useMountEffect'
import { styled } from '@mui/system'
import { GridForm } from './CommunityFormCard'
import { LoadingContext } from '../Context/LoadingContext'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import LoadingProgress from '../Elements/LoadingProgress'
import {
  EventFormCardVariants,
  EventFormValidationMessageTypes,
} from '../Events/EventEnums'
import { dateToDashString, utcDateString } from '../../utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '../../utils/reinterpretYearMonthDayAsLocalTime'

enum EventLocationTypes {
  Local = 'Local',
  Global = 'Global',
}

enum EventRegistrationTypes {
  Open = 'Open',
  Closed = 'Closed',
}

const EventDetailsCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  color: theme.palette.primary.main,
}))

const EventLocationCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(3, 0),
  padding: theme.spacing(3, 4),
  color: theme.palette.primary.main,
}))

const EventDetailsForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& .MuiTextField-root': {
    margin: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiTextField-root': {
      width: `calc(100% - ${theme.spacing(1)})`,
    },
  },
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 1),
}))

const LocationSection = styled('section')(({ theme }) => ({
  gridColumn: '1 / 3',
  [theme.breakpoints.up('lg')]: {
    width: '75% !important',
  },
}))

const EventSection = styled('section')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

export const PublicUrlLink = styled('a')(({ theme }) => ({
  paddingLeft: '15px',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  marginLeft: theme.spacing(2),
  '&:hover': { textDecoration: 'underline' },
}))

const HalfWidthField = styled(TextField)(({ theme }) => ({
  width: '50% !important',
  [theme.breakpoints.down('sm')]: {
    width: '99% !important',
  },
  [theme.breakpoints.down('xs')]: {
    width: '98% !important',
  },
}))
interface EventFormCardProps {
  variant: EventFormCardVariants
  eventDetails?: Event
  eventKey?: number
  updateEventName?: (eventName: string) => void
}

export const EventFormCard: React.FunctionComponent<EventFormCardProps> = (
  props
) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down('sm'))
  const { permissionAbility } = useAuth()
  const { variant, eventDetails: parentEventDetails, eventKey } = props
  const { setSnackbarState, setSnackbarMessage, setSnackbarSeverity } =
    useSnackbarContext()
  const { validationMessages } = useEmailMessage()
  const {
    updateNavigationInformation,
    updateActionConfirmed,
    updateEditMode,
    updateInitiateBlock,
    navigationInformation,
    navigationCallback,
    validateActionConfirmed,
  } = useNavigationAwayContext()

  /** Other Objects */
  const isEditVariant = variant === EventFormCardVariants.EditEvent
  const isCreateVariant = variant === EventFormCardVariants.CreateEvent
  const defaultTimezone = spacetime(undefined, 'America/New_York').timezone()
    .name
  const descriptionMaximumCharacters = 2000
  const additionalInformationMaximumCharacters = 2000

  const defaultErrorMessage = t(
    'Events.EventForm.ErrorMessage',
    'Something went wrong. Please make sure you have filled out every field.'
  )

  const eventRegistrationOptions = [
    t('Event.EventRegistrationOptions.Open', 'Open'),
    t('Event.EventRegistrationOptions.Closed', 'Closed'),
  ]

  const eventTypeOptions = Object.values(EventEventTypeEnum)
  const eventLocationTypeOptions = [
    t('Event.EventLocationTypeOptions.Local', 'Local'),
    t('Event.EventLocationTypeOptions.Global', 'Global'),
  ]

  const eventDeleteConfirmationButtons = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.YesDelete}
      secondaryButtonLabel={TextButtonVariant.NoCancel}
      secondaryClick={() => setIsDeleteEventModalOpen(false)}
    />
  )

  const localEventLinkConfirmationButtons = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.Ok}
      secondaryButtonLabel={TextButtonVariant.Cancel}
      secondaryClick={() => setIsLocalEventLinkModalOpen(false)}
    />
  )

  const canDeleteEvent = permissionAbility.can('delete', 'Event')
  const canCreateEvent = permissionAbility.can('create', 'Event')
  const canEditEvent = permissionAbility.can('edit', 'Event')

  const isRegistrationOpen = parentEventDetails?.registrationOpen
    ? t('Events.EventFormCard.RegistrationStatus.Open', 'Open')
    : t('Events.EventFormCard.RegistrationStatus.Closed', 'Closed')

  const startDateValue =
    parentEventDetails?.startDate.toISOString().slice(0, 10) ??
    todaysDateFormattedLocal()
  const endDateValue =
    parentEventDetails?.endDate?.toISOString().slice(0, 10) ??
    todaysDateFormattedLocal()
  const initialEventDetails = useRef({
    ...parentEventDetails,
    startDate: startDateValue,
    endDate: endDateValue,
    startTime: parentEventDetails?.startTime ?? '08:00',
    endTime: parentEventDetails?.endTime ?? '09:30',
    eventType: !!parentEventDetails ? parentEventDetails?.eventType : '',
    registration: isEditVariant
      ? isRegistrationOpen
      : EventRegistrationTypes.Open,
    capacity: parentEventDetails?.capacity,
    /** We set the state to an empty string as opposed to one of the EventLocationTypes so
     * that we force the user to choose Local or Global when filling out the form.
     */
    eventLocationType: parentEventDetails?.isOnline
      ? EventLocationTypes.Global
      : !parentEventDetails?.isOnline
      ? EventLocationTypes.Local
      : '',
    eventName: parentEventDetails?.name ?? '',
    description: parentEventDetails?.description ?? '',
    additionalInformation: parentEventDetails?.additionalInformation ?? '',
    timezone:
      timezoneTitleCase(parentEventDetails?.timezone ?? '') ?? defaultTimezone,
    onlineEventUrl: parentEventDetails?.onlineEventUrl ?? '',
    publicUrl: parentEventDetails?.publicUrl ?? '',
    hostName: parentEventDetails?.hostName ?? '',
    hostPhone: parentEventDetails?.hostPhone ?? '',
    contactName: parentEventDetails?.contactName ?? '',
    contactEmail: parentEventDetails?.contactEmail ?? '',
    owner: parentEventDetails?.owner ?? '',
    possibleNewOwners: parentEventDetails?.possibleNewOwners ?? [],
    actorkey: parentEventDetails?.actorkey ?? 0,
    registrationCloseDate: parentEventDetails?.registrationCloseDate
      ? dateToDashString(
          reinterpretYearMonthDayAsLocalTime(
            parentEventDetails?.registrationCloseDate
          )
        )
      : undefined,
  })

  const initialAddress = useRef<Address>({
    locationName: parentEventDetails?.address?.locationName ?? '',
    streetAddress1: parentEventDetails?.address?.streetAddress1 ?? '',
    streetAddress2: parentEventDetails?.address?.streetAddress2 ?? '',
    city: parentEventDetails?.address?.city ?? '',
    state: parentEventDetails?.address?.state ?? '',
    zip: parentEventDetails?.address?.zip ?? '',
    countryCode: parentEventDetails?.address?.countryCode ?? '',
  })

  /** State Objects */
  const [address, setAddress] = useState<Address>(initialAddress.current)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)

  const [timeZones, setTimeZones] = useState<Timezones>({} as Timezones)
  const timeZoneOptions =
    timeZones.data?.map((it) => {
      return {
        name: timezoneTitleCase(it.name),
        offset: it.offset,
      }
    }) ?? []

  const [registrants, setRegistrants] = useState<Registrant[]>()

  const [eventDetails, setEventDetails] = useState(initialEventDetails.current)

  // This makes it easier than having to 'eventDetails.' the world
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    eventType,
    registration,
    capacity,
    eventLocationType,
    eventName,
    description,
    additionalInformation,
    timezone,
    onlineEventUrl,
    publicUrl,
    hostName,
    hostPhone,
    contactName,
    contactEmail,
    owner,
    possibleNewOwners,
    actorkey,
    registrationCloseDate,
  } = eventDetails

  /** Field Validity */
  const [eventFieldValidity, setEventFieldValidity] = useState<{
    eventName: FieldValidity
    eventType: FieldValidity
    registration: FieldValidity
    description: FieldValidity
    additionalInformation: FieldValidity
    address: FieldValidity
    eventLocationType: FieldValidity
    onlineEventUrl: FieldValidity
    startDate: FieldValidity
    endDate: FieldValidity
    startTime: FieldValidity
    endTime: FieldValidity
    timezone: FieldValidity
    contactEmail: FieldValidity
    maxCapacity: FieldValidity
    hostName: FieldValidity
    registrationCloseDate: FieldValidity
  }>({
    eventName: { input: true },
    eventType: { input: true },
    registration: { input: true },
    description: { input: true, afterMax: false },
    additionalInformation: { input: true, afterMax: false },
    address: { input: true },
    eventLocationType: { input: true },
    onlineEventUrl: { input: true },
    startDate: { input: true, afterMin: true, beforeMax: true },
    endDate: { input: true, afterMin: true, beforeMax: true },
    startTime: { input: true },
    endTime: { input: true },
    timezone: { input: true },
    contactEmail: { input: true },
    maxCapacity: { input: true },
    hostName: { input: true },
    registrationCloseDate: { input: true },
  })

  const [isFieldDisabled, setIsFieldDisabled] = useState(
    variant === EventFormCardVariants.EditEvent
  )
  const { addLoadingIds } = React.useContext(LoadingContext)

  const availableLoadingIds = useLoadingIds()

  const [loading, setLoading] = useState(false)

  const [isStartTimeGreaterThanEndTime, setIsStartTimeGreaterThanEndTime] =
    useState(false)

  const [isStartDateGreaterThanEndDate, setIsStartDateGreaterThanEndDate] =
    useState(false)

  const [
    isEndDateGreaterOrEqualThanRegistrationCloseDate,
    setIsEndDateGreaterOrEqualThanRegistrationCloseDate,
  ] = useState(false)

  const [
    isRegistrationCloseDateMinorOrEqualThanEndDate,
    setIsRegistrationCloseDateMinorOrEqualThanEndDate,
  ] = useState(false)

  const [isDeleteEventModalOpen, setIsDeleteEventModalOpen] = useState(false)

  const [isLocalEventLinkModalOpen, setIsLocalEventLinkModalOpen] =
    useState(false)

  const [errorMessage, setErrorMessage] = useState('')

  const [textErrorEmail, setTextErrorEmail] = useState<string>('')

  const [reset, setReset] = useState(false)
  const [eventBlockOverride, setEventBlockOverride] = useState(false)

  const navigationHandler = async () => {
    // Needs to be called at the beginning of each handler
    if (!validateActionConfirmed()) {
      return
    }

    // Return to the undefined state so the action is not set again
    updateActionConfirmed(undefined)
    // Always call the callback and then navigate the page.
    navigationCallback()

    if (!isFieldDisabled && navigationInformation.pathname === '') {
      updateEditMode(false)
      setIsFieldDisabled(true)
      setReset(true)
    }
    if (!!navigationInformation.pathname) {
      updateEditMode(false)
      navigate(
        {
          pathname: navigationInformation.pathname,
        },
        {
          ...(!!navigationInformation.state && {
            state: navigationInformation.state,
          }),
        }
      )
    }
  }

  // Include Add Event in the `isEditMode` category of Sidebar navigation blockage
  useMountEffect(() => {
    if (!isEditVariant) {
      updateEditMode(true)
    }
  })

  /*
    Remove navigation listener use to block navigation during form edits. 

    Reason: The upgrade of react-router and react-router-dom to v6 excludes the ability to easily block navigation requests, so, instead of 'fixing'
    all of the current issues with navigation we're going to confirm the actions on save/cancel/save draft, etc when editing and instead of blocking
    just navigate like before we blocked. This means users will lose data if they are editing and decide to click breadcrumbs and sidebars/cancel buttons, etc.
    */
  useNavigationListener({
    overrideBlock: eventBlockOverride || (isEditVariant && isFieldDisabled),
    initiateBlock: false,
    handler: navigationHandler,
  })

  /** Hooks */
  // Fetch Timezones
  const errorMessageFetchTimezones = t(
    'Events.EventFormCard.FetchTimeZones.ErrorMessage',
    'Something went wrong while loading time zones. Please reload the page'
  )

  useEffect(() => {
    if (!!location.state) {
      const eventFromLocation = location.state as unknown as Event & {
        eventLocationType: string
        registration: string
      }
      if (eventFromLocation.createdBy !== undefined) {
        setEventDetails((prevEventDetails) => {
          return {
            ...prevEventDetails,
            eventName: eventFromLocation.name,
            eventType: eventFromLocation.eventType,
            registration: eventFromLocation.registration,
            capacity: eventFromLocation.capacity,
            eventLocationType: eventFromLocation.eventLocationType,
            description: eventFromLocation.description,
            additionalInformation:
              eventFromLocation.additionalInformation ?? '',
            onlineEventUrl: eventFromLocation.onlineEventUrl ?? '',
            hostName: eventFromLocation.hostName ?? '',
            hostPhone: eventFromLocation.hostPhone ?? '',
            contactName: eventFromLocation.contactName ?? '',
            contactEmail: eventFromLocation.contactEmail ?? '',
          }
        })
        if (eventFromLocation.address !== undefined) {
          setAddress(eventFromLocation.address)
        }
      }
    }
  }, [location])

  const fetchTimezones = async () => {
    try {
      const fetchedTimezones = await timezones.fetchTimezones({})
      setTimeZones(fetchedTimezones)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown Code',
        message: (e as unknown as Error).message ?? errorMessageFetchTimezones,
      }
      setSnackbarSeverity(SnackbarSeverity.Error)
      setSnackbarMessage(errorObject.message)
      setSnackbarState(true)
    }
  }

  useLoadingContext({
    asyncFunction: fetchTimezones,
    loadingId: availableLoadingIds.Events.fetchTimezones,
  })

  useEffect(() => {
    addLoadingIds([availableLoadingIds.Events.fetchTimezones])
  }, [
    addLoadingIds,
    availableLoadingIds.Events.fetchTimezones,
    errorMessageFetchTimezones,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
  ])

  // Fetch Registrants...
  const errorMessageFetchRegistrants = t(
    'Events.EventFormCard.FetchRegistrants.ErrorMessage',
    'Something went wrong while loading event registrants. Please reload the page'
  )

  const fetchRegistrants = async () => {
    try {
      const fetchedRegistrants = await events.fetchEventRegistrants({
        eventKey: eventKey ?? 0,
      })
      setRegistrants(fetchedRegistrants.data)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown Code',
        message:
          (e as unknown as Error).message ?? errorMessageFetchRegistrants,
      }
      setSnackbarSeverity(SnackbarSeverity.Error)
      setSnackbarMessage(errorObject.message)
      setSnackbarState(true)
    }
  }

  const refetchRegistrants = () => {
    addLoadingIds([availableLoadingIds.Events.fetchEventRegistrants])
  }

  useLoadingContext({
    asyncFunction: fetchRegistrants,
    loadingId: availableLoadingIds.Events.fetchEventRegistrants,
  })

  useEffect(() => {
    // ...only when editing an Event
    if (isEditVariant) {
      addLoadingIds([availableLoadingIds.Events.fetchEventRegistrants])
    }
  }, [
    eventKey,
    isEditVariant,
    errorMessageFetchRegistrants,
    setSnackbarSeverity,
    setSnackbarMessage,
    setSnackbarState,
    addLoadingIds,
    availableLoadingIds.Events.fetchEventRegistrants,
  ])

  const listOfRegistrants = useMemo(() => {
    return registrants ?? []
  }, [registrants])

  const validationMessageMap = useValidationMessages([
    {
      field: EventFormValidationMessageTypes.Default,
      message: t(
        'Events.EventForm.ErrorMessage',
        'Something went wrong. Please make sure you have filled out the required fields.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventName,
      message: t(
        'Events.EventForm.ValidationMessage.EventName',
        'Event Name cannot be empty.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventType,
      message: t(
        'Events.EventForm.ValidationMessage.EventType',
        'Please select an Event Type.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.Registration,
      message: t(
        'Events.EventForm.ValidationMessage.Registration',
        'Please select a Registration Status.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.MaxCapacity,
      message: t(
        'Events.EventForm.ValidationMessage.MaxCapacity',
        'Max Capacity must be between 1 and 999.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventDescription,
      message: t(
        'Events.EventForm.ValidationMessage.EventDescription',
        'Event Description cannot be empty.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventDescriptionMaxCapacity,
      message: t(
        'Events.EventForm.ValidationMessage.EventDescriptionMaxCapacity',
        `Event Description cannot exceed {{descriptionMaximumCharacters}} characters.`,
        { descriptionMaximumCharacters }
      ),
    },
    {
      field:
        EventFormValidationMessageTypes.EventAdditionalInformationMaxCapacity,
      message: t(
        'Events.EventForm.ValidationMessage.EventAdditionalInformationMaxCapacity',
        'Additional Information cannot exceed {{additionalInformationMaximumCharacters}} characters.',
        { additionalInformationMaximumCharacters }
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventLocationType,
      message: t(
        'Events.EventForm.ValidationMessage.EventLocationType',
        'Please select an Event Location Type.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.OnlineEventUrl,
      message: t(
        'Events.EventForm.ValidationMessage.OnlineEventUrl',
        'Online Event Url cannot be blank.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.Location,
      message: t(
        'Events.EventForm.ValidationMessage.Location',
        'Location cannot be empty.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.StartDate,
      message: t(
        'Events.EventForm.ValidationMessage.StartDate',
        'Please enter a valid Start Date.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EndDate,
      message: t(
        'Events.EventForm.ValidationMessage.EndDate',
        'Please enter a valid End Date.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.BeforeMax,
      message: t(
        'Events.EventForm.ValidationMessage.BeforeMax',
        'Please enter a valid date on or before'
      ),
    },
    {
      field: EventFormValidationMessageTypes.AfterMin,
      message: t(
        'Events.EventForm.ValidationMessage.AfterMin',
        'Please enter a valid date on or after'
      ),
    },
    {
      field: EventFormValidationMessageTypes.StartTime,
      message: t(
        'Events.EventForm.ValidationMessage.StartTime',
        'Please enter a valid Start Time.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EndTime,
      message: t(
        'Events.EventForm.ValidationMessage.EndTime',
        'Please enter a valid End Time.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.TimeZone,
      message: t(
        'Events.EventForm.ValidationMessage.TimeZone',
        'Please select a Time Zone.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.StartDateGreaterThanEndDate,
      message: t(
        'Events.EventForm.ValidationMessage.StartDateGreaterThanEndDate',
        'End Date must be on or after Start Date.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.StartTimeGreaterThanEndTime,
      message: t(
        'Events.EventForm.ValidationMessage.StartTimeGreaterThanEndTime',
        'End Time must be after Start Time.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.ContactEmail,
      message: t(
        'Events.EventForm.ValidationMessage.ContactEmail',
        'Please enter a valid email.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.HostName,
      message: t(
        'Events.EventForm.ValidationMessage.HostName',
        'Host Name cannot be empty.'
      ),
    },
    {
      field:
        EventFormValidationMessageTypes.EndDateGreaterOrEqualThanRegistrationCloseDate,
      message: t(
        'Events.EventForm.ValidationMessage.EndDateGreaterOrEqualThanRegistrationCloseDate',
        'End date must be on or after registration close date.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.RegistrationCloseDate,
      message: t(
        'Events.EventForm.ValidationMessage.RegistrationCloseDate',
        'Registration close date must be before or on the end date.'
      ),
    },
  ])

  /** Methods */
  const resetFields = () => {
    setIsFieldDisabled(true)
    setEventDetails(initialEventDetails.current)
  }

  // When we need to reset the fields, do so.
  useEffect(() => {
    reset && resetFields()
  }, [reset])

  /** Handles 'Cancel' and 'Back' button actions */
  const handleCancelOrBack = () => {
    // Always reset our error message
    setErrorMessage('')

    // If we are creating an event and click cancel the creation, go back to '/events',
    if (!isEditVariant) {
      // Because we are creating an event, initiate a block to confirm navigation away.
      // Cancel only shows if isFieldDisabled, so we can default true
      updateInitiateBlock(true)
      updateNavigationInformation({ pathname: '/events' })
      updateActionConfirmed(true)
    } else {
      if (!isFieldDisabled) {
        // Because we are in edit mode, initiate a block to confirm navigation away
        // Cancel only shows if isFieldDisabled, so we can default true
        updateInitiateBlock(true)
        // It is necessary to set this because, while we are blocking,
        // then another button could set a path to act on after acceptance of navigation.
        updateNavigationInformation({ pathname: '' })
        updateActionConfirmed(true)
      } else {
        // Handling clicking 'Back', so we just go back to Events
        navigate(
          { pathname: '/events' },
          {
            /** Navigation Options */
          }
        )
      }
    }
  }

  const handleEdit = () => {
    setIsFieldDisabled(false)
    updateEditMode(true)
  }

  const handleCopyEvent = () => {
    if (isFieldDisabled) {
      setEventBlockOverride(true)
      navigate(
        {
          pathname: '/events/add-event',
        },
        {
          state: eventDetails,
        }
      )
    } else {
      updateInitiateBlock(true)
      updateNavigationInformation({
        pathname: '/events/add-event',
        state: eventDetails as typeof initialEventDetails.current,
      })
      updateActionConfirmed(true)
    }
  }

  /** Verify the validity of the field information */
  const checkFieldValidity = () => {
    const isOnlineEvent = eventLocationType === EventLocationTypes.Global
    setEventFieldValidity((prevEventFieldValidity) => ({
      ...prevEventFieldValidity,
      eventName: { input: !!eventName },
      eventType: { input: !!eventType },
      registration: { input: !!registration },
      description: { input: !!description },
      maxCapacity: {
        input: validateMaxCapacity(),
        afterMin: !!capacity && capacity > 0,
        beforeMax: !!capacity && capacity <= 999,
      },
      address: {
        input: (!!eventLocationType && isOnlineEvent) || !!address.city,
      },
      eventLocationType: { input: !!eventLocationType },
      onlineEventUrl: {
        input: !isOnlineEvent || (isOnlineEvent && !!onlineEventUrl),
      },
      // We have removed startDate and endDate from checkFieldValidity because their validity is handled in DateField
      startTime: { input: !!startTime },
      endTime: { input: !!endTime },
      timezone: { input: !!timezone },
      contactEmail: {
        input: !!contactEmail && validateContactEmail(contactEmail ?? ''),
      },
      hostName: { input: !!hostName },
    }))
  }

  let registrationCloseDateValue: Date | undefined
  if (registrationCloseDate && registrationCloseDate !== null) {
    registrationCloseDateValue = new Date(registrationCloseDate)
    registrationCloseDateValue.setHours(23)
    registrationCloseDateValue.setMinutes(59)
  }

  const createEvent = async () => {
    setLoading(true)
    try {
      await events.createEvent({
        body: {
          name: eventName ?? '',
          eventType: eventType as EventEventTypeEnum,
          description: description ?? '',
          additionalInformation: additionalInformation ?? '',
          startDate: new Date(startDate ?? ''),
          endDate: new Date(endDate ?? ''),
          startTime: startTime ?? '',
          endTime: endTime ?? '',
          timezone: timezone ?? '',
          isOnline: eventLocationType === EventLocationTypes.Global,
          onlineEventUrl: onlineEventUrl ?? '',
          address:
            eventLocationType === EventLocationTypes.Global
              ? undefined
              : (address as Address),
          registrationOpen: registration === EventRegistrationTypes.Open,
          requiresNda: false,
          // If the field is blank, capacity is "". Since empty string is not valid for the req body, we set it to 'undefined'.
          capacity: capacity || undefined,
          hostName,
          hostPhone,
          contactName,
          contactEmail,
          registrationCloseDate: registrationCloseDateValue,
        },
      })
      navigate(
        {
          pathname: '/events',
        },
        {
          state: {
            snackbarState: true,
            snackbarMessage: t(
              'Events.EventForm.Create.SuccessMessage',
              'Event successfully created.'
            ),
            snackbarSeverity: SnackbarSeverity.Success,
          },
        }
      )
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'UnknownError',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }
      setSnackbarState(true)
      setSnackbarMessage(errorObject.message)
      setSnackbarSeverity(SnackbarSeverity.Error)
    } finally {
      setLoading(false)
    }
  }

  const updateEvent = async () => {
    setLoading(true)
    try {
      await events.updateEvent({
        body: {
          name: eventName ?? '',
          eventType: eventType as EventEventTypeEnum,
          description: description ?? '',
          additionalInformation: additionalInformation ?? '',
          startDate: new Date(startDate ?? ''),
          endDate: new Date(endDate ?? ''),
          startTime: startTime ?? '',
          endTime: endTime ?? '',
          timezone: timezone ?? '',
          isOnline: eventLocationType === EventLocationTypes.Global,
          onlineEventUrl: onlineEventUrl ?? '',
          address:
            eventLocationType === EventLocationTypes.Global
              ? undefined
              : (address as Address),
          eventKey,
          publicUrl: '',
          createdBy: '',
          registrationOpen: registration === EventRegistrationTypes.Open,
          requiresNda: false,
          // If the field is blank, capacity is "". Since empty string is not valid for the req body, we set it to 'undefined'.
          capacity: capacity || undefined,
          hostName,
          hostPhone,
          contactName,
          contactEmail,
          actorkey,
          registrationCloseDate: registrationCloseDateValue,
        },
      })
      setIsFieldDisabled(true)
      updateEditMode(false)
      setSnackbarState(true)
      setSnackbarMessage(
        t(
          'Events.EventForm.Update.SuccessMessage',
          'Event successfully updated.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Success)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'UnknownError',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }
      setSnackbarState(true)
      setSnackbarMessage(errorObject.message)
      setSnackbarSeverity(SnackbarSeverity.Error)
    }
    setLoading(false)
  }

  const allCommonFieldsValid = () => {
    return (
      eventName !== '' &&
      eventType !== '' &&
      description !== '' &&
      eventLocationType !== '' &&
      startDate !== '' &&
      endDate !== '' &&
      startTime !== '' &&
      endTime !== '' &&
      timezone !== '' &&
      contactEmail !== '' &&
      validateContactEmail(contactEmail ?? '') &&
      hostName !== ''
    )
  }

  const areAllFieldsValidOnline = () => {
    return onlineEventUrl !== ''
  }

  const areAllFieldsValidInPerson = () => {
    return address.streetAddress1 !== '' && address.city !== ''
  }

  // Set min & max to two years past and ahead of todays date
  // since we know events wont exceed those limits.
  const maximumDate = useRef(
    new Date(Date.UTC(new Date().getFullYear() + 2, 11, 31))
  )
  const minimumDate = useRef(
    new Date(Date.UTC(new Date().getFullYear() - 2, 0, 1))
  )

  const eventStartDateHelperText = useMemo(() => {
    if (!eventFieldValidity.startDate.input) {
      return validationMessageMap.get(EventFormValidationMessageTypes.StartDate)
    } else if (!eventFieldValidity.startDate.beforeMax) {
      return `${validationMessageMap.get(
        EventFormValidationMessageTypes.BeforeMax
      )} ${dateToDashString(
        reinterpretYearMonthDayAsLocalTime(maximumDate.current)
      )}`
    } else if (!eventFieldValidity.startDate.afterMin) {
      return `${validationMessageMap.get(
        EventFormValidationMessageTypes.AfterMin
      )} ${dateToDashString(
        reinterpretYearMonthDayAsLocalTime(minimumDate.current)
      )}`
    } else {
      return null
    }
  }, [
    eventFieldValidity.startDate.afterMin,
    eventFieldValidity.startDate.beforeMax,
    eventFieldValidity.startDate.input,
    maximumDate,
    minimumDate,
    validationMessageMap,
  ])

  const eventEndDateHelperText = useMemo(() => {
    if (!eventFieldValidity.endDate.input) {
      return validationMessageMap.get(EventFormValidationMessageTypes.StartDate)
    } else if (!eventFieldValidity.endDate.beforeMax) {
      return `${validationMessageMap.get(
        EventFormValidationMessageTypes.BeforeMax
      )} ${dateToDashString(
        reinterpretYearMonthDayAsLocalTime(maximumDate.current)
      )}`
    } else if (!eventFieldValidity.endDate.afterMin) {
      return `${validationMessageMap.get(
        EventFormValidationMessageTypes.AfterMin
      )} ${dateToDashString(
        reinterpretYearMonthDayAsLocalTime(minimumDate.current)
      )}`
    } else if (isStartDateGreaterThanEndDate) {
      return validationMessageMap.get(
        EventFormValidationMessageTypes.StartDateGreaterThanEndDate
      )
    } else if (isEndDateGreaterOrEqualThanRegistrationCloseDate) {
      return validationMessageMap.get(
        EventFormValidationMessageTypes.EndDateGreaterOrEqualThanRegistrationCloseDate
      )
    } else {
      return null
    }
  }, [
    eventFieldValidity.endDate.input,
    eventFieldValidity.endDate.beforeMax,
    eventFieldValidity.endDate.afterMin,
    isStartDateGreaterThanEndDate,
    isEndDateGreaterOrEqualThanRegistrationCloseDate,
    validationMessageMap,
    maximumDate,
    minimumDate,
  ])

  const validateTime = () => {
    if (
      startDate === endDate &&
      timeStringToNumber(endTime as string) <=
        timeStringToNumber(startTime as string)
    ) {
      setIsStartTimeGreaterThanEndTime(true)
    } else {
      setIsStartTimeGreaterThanEndTime(false)
    }
  }

  const validateMaxCapacity = (): boolean => {
    // Capacity is valid if:
    // * falsy (except 0)
    // * is within range
    return (
      (!capacity && capacity !== 0) ||
      (!!capacity && capacity > 0 && capacity <= 999)
    )
  }

  const validateContactEmail = (email: string) => {
    const resultMsg = validateEmailText(email)
    setTextErrorEmail(resultMsg)
    return resultMsg === ''
  }
  const isLocalOnlineEvent = () => {
    return (
      eventLocationType === EventLocationTypes.Local &&
      areAllFieldsValidOnline()
    )
  }

  const handleSave = async () => {
    setIsLocalEventLinkModalOpen(false)
    setEventBlockOverride(true)
    updateInitiateBlock(false)
    updateEditMode(false)

    validateTime()

    if (isEditVariant) {
      // Validate fields

      checkFieldValidity()
      if (
        eventLocationType === EventLocationTypes.Local &&
        areAllFieldsValidInPerson() &&
        validateMaxCapacity() &&
        validateContactEmail(contactEmail ?? '')
      ) {
        updateEvent()
        props.updateEventName?.(eventName)
      } else if (
        eventLocationType === EventLocationTypes.Global &&
        areAllFieldsValidOnline() &&
        validateMaxCapacity() &&
        validateContactEmail(contactEmail ?? '')
      ) {
        updateEvent()
        props.updateEventName?.(eventName)
      } else {
        const e = {
          code: 'UnknownError',
          message: defaultErrorMessage,
        }
        setSnackbarState(true)
        setSnackbarMessage((e as unknown as Error).message)
        setSnackbarSeverity(SnackbarSeverity.Error)
      }
    } else {
      // Validate fields
      checkFieldValidity()
      validateContactEmail(contactEmail ?? '')

      if (
        eventDetails.eventLocationType === EventLocationTypes.Local &&
        allCommonFieldsValid() &&
        areAllFieldsValidInPerson() &&
        validateMaxCapacity() &&
        validateContactEmail(contactEmail ?? '')
      ) {
        createEvent()
      } else if (
        eventLocationType === EventLocationTypes.Global &&
        allCommonFieldsValid() &&
        areAllFieldsValidOnline() &&
        validateMaxCapacity() &&
        validateContactEmail(contactEmail ?? '')
      ) {
        createEvent()
      } else {
        const e = {
          code: 'UnknownError',
          message: defaultErrorMessage,
        }
        setSnackbarState(true)
        setSnackbarMessage((e as unknown as Error).message)
        setSnackbarSeverity(SnackbarSeverity.Error)
      }
    }
  }

  const onOwnerChange = (
    event: SelectChangeEvent<unknown>,
    child: ReactNode
  ) => {
    const actorKey: number = +((child as ReactElement).key
      ?.toString()
      .slice(2) as string)

    setEventDetails((prevEventDetails) => {
      return {
        ...prevEventDetails,
        actorkey: actorKey,
        owner: event.target.value as string,
      }
    })
  }

  const validateInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.name) {
      case 'description':
        event.target.value.length >= descriptionMaximumCharacters
          ? setEventFieldValidity({
              ...eventFieldValidity,
              description: { input: true, afterMax: true },
            })
          : setEventFieldValidity({
              ...eventFieldValidity,
              description: { input: true, afterMax: false },
            })

        break
      case 'additionalInformation':
        event.target.value.length >= additionalInformationMaximumCharacters
          ? setEventFieldValidity({
              ...eventFieldValidity,
              additionalInformation: { input: true, afterMax: true },
            })
          : setEventFieldValidity({
              ...eventFieldValidity,
              additionalInformation: { input: true, afterMax: false },
            })

        break

      default:
        break
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setEventDetails((prevEventDetails) => {
      return {
        ...prevEventDetails,
        [name]: name === 'contactEmail' ? value.trim() : value,
      }
    })
    validateInputChange(event)
  }

  const handleRegistrationCloseDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value?: string,
    validity?: FieldValidity
  ) => {
    setIsRegistrationCloseDateMinorOrEqualThanEndDate(
      // Since comparing string, '<=' or '>=' doesn't work
      !(event.target.value < endDate || event.target.value == endDate)
    )

    setEventDetails((prevEventDetails) => {
      return {
        ...prevEventDetails,
        [event.target.name]: value ?? event.target.value,
      }
    })

    setEventFieldValidity((prevEventFieldValidity) => {
      return {
        ...prevEventFieldValidity,
        [event.target.name]: validity ?? {
          input: true,
        },
      }
    })
  }

  const handleDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value?: string,
    validity?: FieldValidity
  ) => {
    setIsStartDateGreaterThanEndDate(
      event.target.name === 'startDate'
        ? new Date(event.target.value) > new Date(endDate)
        : new Date(startDate) > new Date(event.target.value)
    )
    if (event.target.name === 'endDate' && registrationCloseDate) {
      setIsEndDateGreaterOrEqualThanRegistrationCloseDate(
        // Since comparing string, '<=' or '>=' doesn't work
        !(
          registrationCloseDate < event.target.value ||
          registrationCloseDate == event.target.value
        )
      )
    }

    setEventDetails((prevEventDetails) => {
      return {
        ...prevEventDetails,
        [event.target.name]: value ?? event.target.value,
      }
    })

    setEventFieldValidity((prevEventFieldValidity) => {
      return {
        ...prevEventFieldValidity,
        [event.target.name]: validity ?? {
          input: true,
          afterMin: true,
          beforeMax: true,
        },
      }
    })
  }

  const handleYesDelete = async (event: FormEvent<HTMLDivElement>) => {
    event.preventDefault()
    setLoading(true)
    try {
      await events.deleteEvent({ eventKey: eventKey as number })
      navigate(
        {
          pathname: '/events',
        },
        {
          state: {
            snackbarState: true,
            snackbarMessage: t(
              'Events.EventForm.Delete.SuccessMessage',
              'Event successfully deleted.'
            ),
            snackbarSeverity: SnackbarSeverity.Success,
          },
        }
      )
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'UnknownError',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }

      setErrorMessage(errorObject.message)
    }
    setLoading(false)
  }

  const eventFormButtons = () => {
    let primaryButtonLabel = undefined
    let secondaryButtonLabel:
      | TextButtonVariant
      | ContainedButtonVariant
      | OutlinedButtonVariant
      | undefined = undefined
    let secondaryClick = undefined
    let tertiaryButtonLabel = undefined
    let tertiaryClick = undefined
    let quaternaryButtonLabel = TextButtonVariant.Back
    let quaternaryClick = handleCancelOrBack

    if (isEditVariant && !isFieldDisabled) {
      primaryButtonLabel = ContainedButtonVariant.Publish
      if (canCreateEvent && canEditEvent) {
        secondaryButtonLabel = OutlinedButtonVariant.CopyEvent
        secondaryClick = handleCopyEvent
      }
      if (listOfRegistrants.length === 0 && canDeleteEvent) {
        tertiaryButtonLabel = ContainedButtonVariant.Delete
        tertiaryClick = () => setIsDeleteEventModalOpen(true)
      }
      quaternaryButtonLabel = TextButtonVariant.Cancel
      quaternaryClick = handleCancelOrBack
    } else if (isCreateVariant) {
      primaryButtonLabel = ContainedButtonVariant.Publish
      quaternaryButtonLabel = TextButtonVariant.Cancel
      quaternaryClick = handleCancelOrBack
    } else {
      if (canEditEvent) {
        primaryButtonLabel = ContainedButtonVariant.Edit
        if (canCreateEvent) {
          secondaryButtonLabel = OutlinedButtonVariant.CopyEvent
          secondaryClick = handleCopyEvent
        }
      }
      if (listOfRegistrants.length === 0 && canDeleteEvent) {
        tertiaryButtonLabel = ContainedButtonVariant.Delete
        tertiaryClick = () => setIsDeleteEventModalOpen(true)
      }
    }

    const areTextLengthValid = !(
      !!eventFieldValidity.additionalInformation.afterMax ||
      !!eventFieldValidity.description.afterMax
    )

    const eventExpirationDateAndTime = spacetime(
      utcDateString(new Date(endDate)),
      timezone
    ).time(endTime)
    /* disables the edit button if the event has expired,
    validates taking into consideration the time zone of both
     the event and the user who wants to edit it*/
    const disablePrimaryButton =
      spacetime.now().isAfter(eventExpirationDateAndTime) ||
      !areTextLengthValid ||
      isRegistrationCloseDateMinorOrEqualThanEndDate

    return (
      <ActionButtons
        primaryButtonLabel={primaryButtonLabel}
        disablePrimaryButton={disablePrimaryButton}
        secondaryButtonLabel={secondaryButtonLabel}
        tertiaryButtonLabel={tertiaryButtonLabel}
        quaternaryButtonLabel={quaternaryButtonLabel}
        secondaryClick={secondaryClick}
        tertiaryClick={tertiaryClick}
        quaternaryClick={quaternaryClick}
      />
    )
  }

  if (loading) {
    return <LoadingProgress />
  }

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteEventModalOpen}
        handleFormSubmit={handleYesDelete}
        dialogContent={!!errorMessage && <ErrorAlert error={errorMessage} />}
        dialogActions={eventDeleteConfirmationButtons}
        dialogTitle={t(
          'Events.Delete.Confirmation',
          'Are you sure you want to delete this event?'
        )}
      />

      <ConfirmationModal
        isOpen={isLocalEventLinkModalOpen}
        handleFormSubmit={(event) => {
          event.preventDefault()
          isEditVariant && isFieldDisabled ? handleEdit() : handleSave()
        }}
        dialogContent={!!errorMessage && <ErrorAlert error={errorMessage} />}
        dialogActions={localEventLinkConfirmationButtons}
        dialogTitle={t(
          'Events.AddEvent.Link.Confirmation',
          'You have added a meeting link. This event will become a Local ONLINE event and the meeting link will be sent to the Registrant instead of the physical address.'
        )}
      />

      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          isEditVariant && isFieldDisabled
            ? handleEdit()
            : isLocalOnlineEvent()
            ? setIsLocalEventLinkModalOpen(true)
            : handleSave()
        }}
        noValidate
        autoComplete="off"
        aria-labelledby="eventDetailsHeader"
      >
        {isMobileOrSmaller && <Box>{eventFormButtons()}</Box>}
        <EventDetailsCard>
          {loading ? <LoadingProgress /> : null}
          <CardFormHeader
            header={
              <Header
                headerName={t(
                  'Events.AddEvent.EventDetails.Header',
                  'Event Details'
                )}
                component="h2"
                variant={HeaderVariant.Card}
              />
            }
            buttons={
              <Box
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'end',
                    marginBottom: theme.spacing(2),
                  },
                }}
              >
                {!isMobileOrSmaller && <Box>{eventFormButtons()}</Box>}
              </Box>
            }
          />
          <EventSection aria-labelledby="eventDetailsHeader">
            <Box
              sx={{
                background: theme.palette.primary.main,
                ...(eventLocationType === EventLocationTypes.Global && {
                  background: theme.palette.customBackground.logo,
                }),
                width: 290,
                height: 290,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  height: 80,
                },
              }}
            >
              <img
                src={Logo}
                alt={t('Events.EventImage.AltText', 'Event Image')}
                style={{
                  ...(eventLocationType === EventLocationTypes.Global && {
                    /** This is the filter we need to use to convert our primary color's hex (#051f42)
                     * to the same color but filtered for an SVG.
                     **/
                    filter:
                      'brightness(0) saturate(100%) invert(9%) sepia(18%) saturate(6857%) hue-rotate(200deg) brightness(98%) contrast(99%)',
                  }),
                  width: !isMobileOrSmaller ? 160 : 70,
                  // breakpoints not being applied on img tags
                  // [theme.breakpoints.down('sm')]: {
                  //   width: 70,
                  // },
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                '& .MuiTextField-root': {
                  width: 525,
                  margin: theme.spacing(1),
                },
                [theme.breakpoints.down('sm')]: {
                  '& .MuiTextField-root': {
                    width: `calc(100% - ${theme.spacing(1)})`,
                  },
                },
              }}
            >
              <TextField
                label={t('Events.EventForm.FormField.name', 'Event Name')}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.name',
                    'Event Name'
                  ),
                }}
                name="eventName"
                variant="filled"
                value={eventName}
                onChange={handleInputChange}
                disabled={isFieldDisabled}
                error={!eventFieldValidity.eventName.input}
                helperText={
                  !eventFieldValidity.eventName.input
                    ? validationMessageMap.get(
                        EventFormValidationMessageTypes.EventName
                      )
                    : null
                }
              />

              <TextField
                label={t('Events.EventForm.FormField.EventType', 'Event Type')}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.EventType',
                    'Event Type'
                  ),
                }}
                variant="filled"
                select={!isFieldDisabled}
                value={eventType}
                name="eventType"
                onChange={handleInputChange}
                disabled={isFieldDisabled}
                error={!eventFieldValidity.eventType.input}
                helperText={
                  !eventFieldValidity.eventType.input
                    ? validationMessageMap.get(
                        EventFormValidationMessageTypes.EventType
                      )
                    : null
                }
              >
                {eventTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Box
                sx={{
                  display: 'flex',
                  [theme.breakpoints.down('sm')]: {
                    display: 'grid',
                  },
                }}
              >
                <HalfWidthField
                  label={t(
                    'Events.EventForm.FormField.Registration',
                    'Registration'
                  )}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.Registration',
                      'Registration'
                    ),
                  }}
                  variant="filled"
                  select={!isFieldDisabled}
                  value={registration}
                  name="registration"
                  onChange={handleInputChange}
                  disabled={isFieldDisabled}
                  error={!eventFieldValidity.registration.input}
                  helperText={
                    !eventFieldValidity.registration.input
                      ? validationMessageMap.get(
                          EventFormValidationMessageTypes.Registration
                        )
                      : null
                  }
                >
                  {eventRegistrationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </HalfWidthField>

                <DateField
                  id="registrationCloseDateField"
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.RegistrationDate',
                      'Registration Close Date'
                    ),
                  }}
                  sx={{
                    width: '50% !important',
                    [theme.breakpoints.down('sm')]: {
                      width: '99% !important',
                    },
                    [theme.breakpoints.down('xs')]: {
                      width: '98% !important',
                    },
                  }}
                  variant="filled"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  disabled={isFieldDisabled}
                  value={registrationCloseDate ?? ''}
                  name="registrationCloseDate"
                  error={isRegistrationCloseDateMinorOrEqualThanEndDate}
                  onChangeForParent={handleRegistrationCloseDateChange}
                  helperTextForParent={
                    isRegistrationCloseDateMinorOrEqualThanEndDate
                      ? validationMessageMap.get(
                          EventFormValidationMessageTypes.RegistrationCloseDate
                        )
                      : null
                  }
                  dateVariant={DateFieldVariant.RegistrationCloseDate}
                />
              </Box>
              {isEditVariant ? (
                <FormControl
                  component="fieldset"
                  sx={{
                    margin: '8px',
                    [theme.breakpoints.down('sm')]: {
                      margin: '11px 0px 0px 7px',
                    },
                  }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      color: '#8a929f',
                      transform: 'translate(12px, -2px) scale(0.75)',
                    }}
                    aria-label={t(
                      'Events.EventForm.AriaLabel.PublicUrl',
                      'Public Url'
                    )}
                  >
                    {t('Events.EventForm.FormField.PublicUrl', 'Link')}
                  </FormLabel>
                  <FormGroup>
                    <PublicUrlLink
                      id={publicUrl}
                      href={publicUrl}
                      target="_blank"
                    >
                      {publicUrl}
                    </PublicUrlLink>
                  </FormGroup>
                  <hr />
                </FormControl>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  [theme.breakpoints.down('sm')]: {
                    display: 'grid',
                  },
                }}
              >
                <HalfWidthField
                  label={t('Events.EventForm.FormField.Capacity', 'Capacity')}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.Capacity',
                      'Capacity'
                    ),
                  }}
                  variant="filled"
                  value={capacity?.toString() || ''}
                  name="capacity"
                  onChange={handleInputChange}
                  disabled={isFieldDisabled}
                  error={!eventFieldValidity.maxCapacity.input}
                  helperText={
                    !eventFieldValidity.maxCapacity.input
                      ? validationMessageMap.get(
                          EventFormValidationMessageTypes.MaxCapacity
                        )
                      : null
                  }
                />
                <HalfWidthField
                  label={t(
                    'Events.EventForm.FormField.EventLocationType',
                    'Event Location Type'
                  )}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.EventLocationType',
                      'Event Location Type'
                    ),
                  }}
                  variant="filled"
                  disabled={isFieldDisabled}
                  select={!isFieldDisabled}
                  value={eventLocationType}
                  name="eventLocationType"
                  onChange={handleInputChange}
                  error={!eventFieldValidity.eventLocationType.input}
                  helperText={
                    !eventFieldValidity.eventLocationType.input
                      ? validationMessageMap.get(
                          EventFormValidationMessageTypes.EventLocationType
                        )
                      : null
                  }
                >
                  {eventLocationTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </HalfWidthField>
                {possibleNewOwners.length > 0 && (
                  <HalfWidthField
                    label={t(
                      'Events.EventForm.FormField.PossibleNew',
                      'Event Owner'
                    )}
                    inputProps={{
                      'aria-label': t(
                        'Events.EventForm.AriaLabel.PossibleNew',
                        'Event Owner'
                      ),
                    }}
                    variant="filled"
                    disabled={isFieldDisabled}
                    select={!isFieldDisabled}
                    SelectProps={{ onChange: onOwnerChange }}
                    name="eventOwner"
                    value={owner}
                  >
                    {possibleNewOwners
                      .sort((a, b) => a.lastName.localeCompare(b.lastName))
                      .map((option) => (
                        <MenuItem
                          key={option.actorKey}
                          value={`${option.firstName} ${option.lastName}`}
                        >
                          {`${option.firstName} ${option.lastName}`}
                        </MenuItem>
                      ))}
                  </HalfWidthField>
                )}
              </Box>
            </Box>
          </EventSection>
        </EventDetailsCard>
      </form>
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false)
        }}
        initialAddress={address}
        onAddressConfirm={(value) => setAddress(value)}
      />
      <EventLocationCard>
        <CardFormHeader
          header={
            <Header
              headerName={t(
                'Events.AddEvent.Description.Header',
                'Description'
              )}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
        />
        <section aria-labelledby="eventDescriptionFormHeader">
          <EventDetailsForm
            noValidate
            autoComplete="off"
            aria-labelledby="eventDescriptionFormHeader"
          >
            <TextField
              id="standard-multiline-static"
              label={t(
                'Events.EventForm.FormField.EventDescription',
                'Event Description'
              )}
              multiline
              rows={6}
              variant="filled"
              value={description}
              name="description"
              onChange={handleInputChange}
              disabled={isFieldDisabled}
              error={
                !eventFieldValidity.description.input ||
                eventFieldValidity.description.afterMax
              }
              helperText={
                !eventFieldValidity.description.input
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.EventDescription
                    )
                  : eventFieldValidity.description.afterMax
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.EventDescriptionMaxCapacity
                    )
                  : null
              }
            />
          </EventDetailsForm>
          <StyledDivider />
        </section>
        <CardFormHeader
          header={
            <Header
              id="additionalInformationFormHeader"
              headerName={t(
                'Events.AddEvent.additionalInformation.Header',
                'Additional Information'
              )}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
        />
        <section aria-labelledby="additionalInformationFormHeader">
          <EventDetailsForm
            noValidate
            autoComplete="off"
            aria-labelledby="additionalInformationFormHeader"
          >
            <TextField
              id="additional-information"
              label={t(
                'Events.EventForm.FormField.additionalInformation',
                'Event Additional Information'
              )}
              multiline
              rows={6}
              variant="filled"
              value={additionalInformation}
              name="additionalInformation"
              onChange={handleInputChange}
              disabled={isFieldDisabled}
              error={eventFieldValidity.additionalInformation.afterMax}
              helperText={
                eventFieldValidity.additionalInformation.afterMax
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.EventAdditionalInformationMaxCapacity
                    )
                  : null
              }
            />
          </EventDetailsForm>
          <StyledDivider />
        </section>
        <CardFormHeader
          header={
            <Header
              headerName={t('Events.AddEvent.Location.Header', 'Location')}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            !isFieldDisabled &&
            eventLocationType !== EventLocationTypes.Global && (
              <OutlinedButton
                id="editLocation"
                variant={OutlinedButtonVariant.EditLocation}
                onClick={() => setIsAddressModalOpen(true)}
              />
            )
          }
        />

        {!eventFieldValidity.address.input && (
          <ErrorAlert
            error={
              validationMessageMap.get(
                EventFormValidationMessageTypes.Location
              ) ?? ''
            }
          />
        )}
        <section aria-labelledby="eventLocationFormHeader">
          <EventDetailsForm
            noValidate
            autoComplete="off"
            aria-labelledby="eventLocationFormHeader"
          >
            {eventLocationType !== EventLocationTypes.Global && (
              <GridForm>
                <TextField
                  label={t(
                    'Events.EventForm.FormField.Address',
                    'Street Address'
                  )}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.StreetAddress1',
                      'Street Address 1'
                    ),
                  }}
                  variant="filled"
                  disabled
                  sx={{
                    gridColumn: '1',
                    gridRow: '1',
                  }}
                  value={address.streetAddress1}
                />
                <TextField
                  label={t(
                    'Events.EventForm.FormField.Address2',
                    'Building, Suite, etc.'
                  )}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.StreetAddress2',
                      'Street Address 2'
                    ),
                  }}
                  variant="filled"
                  disabled
                  sx={{
                    gridColumn: '1',
                    gridRow: '2',
                  }}
                  value={address.streetAddress2}
                />
                <TextField
                  label={t('Events.EventForm.FormField.City', 'City')}
                  inputProps={{
                    'aria-label': t('Events.EventForm.AriaLabel.City', 'City'),
                  }}
                  variant="filled"
                  disabled
                  sx={{
                    gridColumn: '1',
                    gridRow: '3',
                  }}
                  value={address.city}
                />
                <TextField
                  label={t('Events.EventForm.FormField.Country', 'Country')}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.Country',
                      'Country'
                    ),
                  }}
                  variant="filled"
                  disabled
                  sx={{
                    gridColumn: isMobileOrSmaller ? '1' : '2',
                    gridRow: isMobileOrSmaller ? '4' : '1',
                  }}
                  value={address.countryCode}
                />
                <TextField
                  label={t(
                    'Events.EventForm.FormField.State',
                    'State/Province'
                  )}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.State',
                      'State/Province'
                    ),
                  }}
                  variant="filled"
                  disabled
                  sx={{
                    gridColumn: isMobileOrSmaller ? '1' : '2',
                    gridRow: isMobileOrSmaller ? '5' : '2',
                  }}
                  value={address.state}
                />
                <TextField
                  label={t(
                    'Events.EventForm.FormField.PostalCode',
                    'Postal Code'
                  )}
                  inputProps={{
                    'aria-label': t(
                      'Events.EventForm.AriaLabel.PostalCode',
                      'Postal Code'
                    ),
                  }}
                  variant="filled"
                  disabled
                  sx={{
                    gridColumn: isMobileOrSmaller ? '1' : '2',
                    gridRow: isMobileOrSmaller ? '6' : '3',
                  }}
                  value={address.zip}
                />
              </GridForm>
            )}
            <LocationSection>
              {eventLocationType !== EventLocationTypes.Global && (
                <StyledDivider />
              )}
              <TextField
                id="linkField"
                label={t(
                  'Events.EventForm.FormField.MeetingLink',
                  'Meeting Link'
                )}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.MeetingLink',
                    'Meeting Link'
                  ),
                }}
                fullWidth
                variant="filled"
                disabled={isFieldDisabled}
                value={onlineEventUrl}
                name="onlineEventUrl"
                onChange={handleInputChange}
                error={!eventFieldValidity.onlineEventUrl.input}
                helperText={
                  !eventFieldValidity.onlineEventUrl.input
                    ? validationMessageMap.get(
                        EventFormValidationMessageTypes.OnlineEventUrl
                      )
                    : null
                }
              />
            </LocationSection>
          </EventDetailsForm>
        </section>
        <StyledDivider />
        <CardFormHeader
          header={
            <Header
              id="eventDateAndTimeFormHeader"
              headerName={t(
                'Events.AddEvent.DateAndTime.Header',
                'Date and Time'
              )}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
        />
        <section aria-labelledby="eventDateAndTimeFormHeader">
          <EventDetailsForm
            noValidate
            autoComplete="off"
            aria-labelledby="eventDateAndTimeFormHeader"
            sx={{
              display: 'grid',
            }}
          >
            <DateField
              dateVariant={DateFieldVariant.EventStartDate}
              type="date"
              disabled={isFieldDisabled}
              sx={{
                gridColumn: '1',
                gridRow: '1',
              }}
              value={startDate}
              name="startDate"
              onChangeForParent={handleDateChange}
              error={
                !eventFieldValidity.startDate.input ||
                !eventFieldValidity.startDate.beforeMax ||
                !eventFieldValidity.startDate.afterMin
              }
              helperTextForParent={eventStartDateHelperText}
            />
            <DateField
              dateVariant={DateFieldVariant.EventEndDate}
              type="date"
              disabled={isFieldDisabled}
              sx={{
                gridColumn: '1',
                gridRow: '2',
              }}
              value={endDate}
              name="endDate"
              onChangeForParent={handleDateChange}
              error={!!eventEndDateHelperText}
              helperTextForParent={eventEndDateHelperText}
            />

            <TextField
              id="startTime"
              label={t('Events.EventForm.FormField.State', 'Start Time')}
              inputProps={{
                'aria-label': t(
                  'Events.EventForm.AriaLabel.StartTime',
                  'Start Time'
                ),
              }}
              variant="filled"
              type="time"
              value={startTime}
              name="startTime"
              onChange={handleInputChange}
              disabled={isFieldDisabled}
              sx={{
                gridColumn: isMobileOrSmaller ? '1' : '2',
                gridRow: isMobileOrSmaller ? '3' : '1',
              }}
              error={!eventFieldValidity.startTime.input}
              helperText={
                !eventFieldValidity.startTime.input
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.StartTime
                    )
                  : null
              }
            />

            <TextField
              id="endTime"
              label={t('Events.EventForm.FormField.PostalCode', 'End Time')}
              inputProps={{
                'aria-label': t(
                  'Events.EventForm.AriaLabel.EndTime',
                  'End Time'
                ),
              }}
              variant="filled"
              type="time"
              value={endTime}
              name="endTime"
              onChange={handleInputChange}
              disabled={isFieldDisabled}
              sx={{
                gridColumn: isMobileOrSmaller ? '1' : '2',
                gridRow: isMobileOrSmaller ? '4' : '2',
              }}
              error={
                !eventFieldValidity.endTime.input ||
                isStartTimeGreaterThanEndTime
              }
              helperText={
                !eventFieldValidity.endTime.input
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.EndTime
                    )
                  : isStartTimeGreaterThanEndTime
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.StartTimeGreaterThanEndTime
                    )
                  : null
              }
            />
            <TextField
              label={t('Events.EventForm.FormField.TimeZone', 'Time Zone')}
              inputProps={{
                'aria-label': t(
                  'Events.EventForm.AriaLabel.Timezone',
                  'Timezone'
                ),
              }}
              variant="filled"
              select={!isFieldDisabled}
              disabled={isFieldDisabled}
              sx={{
                gridColumn: isMobileOrSmaller ? '1' : '3',
                gridRow: isMobileOrSmaller ? '5' : '1',
              }}
              value={timeZoneOptions.length > 0 ? timezone : ''}
              name="timezone"
              onChange={handleInputChange}
              error={!eventFieldValidity.timezone.input}
              helperText={
                !eventFieldValidity.timezone.input
                  ? validationMessageMap.get(
                      EventFormValidationMessageTypes.TimeZone
                    )
                  : null
              }
            >
              {timeZoneOptions.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </EventDetailsForm>
        </section>

        <StyledDivider />

        <CardFormHeader
          header={
            <Header
              headerName={t(
                'Events.AddEvent.HostAndContact.Header',
                'Host and Contact Information'
              )}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
        />
        <section aria-labelledby="eventHostAndContactFormHeader">
          <EventDetailsForm
            noValidate
            autoComplete="off"
            aria-labelledby="eventHostAndContactFormHeader"
          >
            <GridForm>
              <TextField
                label={t('Events.EventForm.FormField.HostName', 'Host Name')}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.HostName',
                    'Host Name'
                  ),
                }}
                variant="filled"
                value={hostName}
                name="hostName"
                onChange={handleInputChange}
                disabled={isFieldDisabled}
                error={!eventFieldValidity.hostName.input}
                helperText={
                  !eventFieldValidity.hostName.input
                    ? validationMessageMap.get(
                        EventFormValidationMessageTypes.HostName
                      )
                    : null
                }
              />
              <TextField
                label={t(
                  'Events.EventForm.FormField.ContactName',
                  'Contact Name'
                )}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.ContactName',
                    'Contact Name'
                  ),
                }}
                variant="filled"
                value={contactName}
                name="contactName"
                onChange={handleInputChange}
                disabled={isFieldDisabled}
              />
              <TextField
                label={t('Events.EventForm.FormField.HostPhone', 'Host Phone')}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.HostPhone',
                    'Host Phone'
                  ),
                }}
                variant="filled"
                value={hostPhone}
                name="hostPhone"
                onChange={handleInputChange}
                disabled={isFieldDisabled}
              />
              <TextField
                id="emailContact"
                label={t(
                  'Events.EventForm.FormField.ContactEmail',
                  'Contact Email'
                )}
                inputProps={{
                  'aria-label': t(
                    'Events.EventForm.AriaLabel.ContactEmail',
                    'Contact Email'
                  ),
                }}
                variant="filled"
                value={contactEmail ?? ''}
                name="contactEmail"
                onChange={handleInputChange}
                disabled={isFieldDisabled}
                error={!eventFieldValidity.contactEmail.input}
                helperText={validationMessages(textErrorEmail)}
              />
            </GridForm>
          </EventDetailsForm>
        </section>
      </EventLocationCard>
      {isEditVariant ? (
        <RegistrantsTable
          {...props}
          eventRegistrants={listOfRegistrants.length}
          eventCapacity={capacity}
          registrants={listOfRegistrants as Registrant[]}
          refetch={refetchRegistrants}
        />
      ) : null}
    </>
  )
}

export default EventFormCard
